import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryClient';
import { logAction } from '../lib/actionLogger';

export function useLinks() {
  return useQuery({
    queryKey: queryKeys.links.all,
    queryFn: () => api.get('/links'),
  });
}

export function useLink(id) {
  return useQuery({
    queryKey: queryKeys.links.detail(id),
    queryFn: () => api.get(`/links/${id}`),
    enabled: !!id,
  });
}

export function useLinkClicks(id, { limit = 50, offset = 0, from, to } = {}) {
  const params = new URLSearchParams({ limit, offset });
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  return useQuery({
    queryKey: queryKeys.links.clicks(id, limit, offset, from, to),
    queryFn: () => api.get(`/links/${id}/clicks?${params}`),
    enabled: !!id,
  });
}

export function useLinkStats(id, days = 30) {
  return useQuery({
    queryKey: queryKeys.links.stats(id, days),
    queryFn: () => api.get(`/links/${id}/stats?days=${days}`),
    enabled: !!id,
  });
}

export function useCreateLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.post('/links', data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.links.all });
      logAction({
        action: 'Create Short Link',
        description: `Created short link /r/${data.short_code} → ${data.destination_url}`,
        details: {
          id: data.id,
          short_code: data.short_code,
          destination_url: data.destination_url,
          title: data.title,
          tags: data.tags,
        },
      });
    },
  });
}

export function useUpdateLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...updates }) => api.put(`/links/${id}`, updates),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.links.all });
      qc.invalidateQueries({ queryKey: queryKeys.links.detail(data.id) });
      logAction({
        action: 'Update Short Link',
        description: `Updated short link /r/${data.short_code}`,
        details: {
          id: data.id,
          short_code: data.short_code,
          destination_url: data.destination_url,
          title: data.title,
          is_active: data.is_active,
        },
      });
    },
  });
}

export function useDeleteLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.del(`/links/${id}`),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.links.all });
      logAction({
        action: 'Delete Short Link',
        description: `Deleted short link ${id}`,
        details: { id },
      });
    },
  });
}
